import { template as _$template } from "solid-js/web";
import { delegateEvents as _$delegateEvents } from "solid-js/web";
import { setStyleProperty as _$setStyleProperty } from "solid-js/web";
import { effect as _$effect } from "solid-js/web";
import { memo as _$memo } from "solid-js/web";
import { insert as _$insert } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<p class="assets-viewer__status assets-viewer__status--error">Failed to load the bake manifest: `),
  _tmpl$2 = /*#__PURE__*/_$template(`<p class=assets-viewer__status>Loading manifest…`),
  _tmpl$3 = /*#__PURE__*/_$template(`<div class="assets-viewer__main assets-viewer__main--unbaked">`),
  _tmpl$4 = /*#__PURE__*/_$template(`<div class="assets-viewer__main assets-viewer__main--props">`),
  _tmpl$5 = /*#__PURE__*/_$template(`<div class=assets-viewer__main><section class=assets-viewer__list-pane><div class=assets-viewer__list-header><input type=search class=assets-viewer__search placeholder="Search by name or source path…"><span class=assets-viewer__list-count></span></div><ul class=assets-viewer__entry-list tabindex=0>`),
  _tmpl$6 = /*#__PURE__*/_$template(`<div class=assets-viewer><nav class=assets-viewer__sidebar aria-label="Asset categories"><h2>Categories</h2><ul><li><button type=button class=assets-viewer__category title="Enhanced models with no baked asset behind them — built from a citable object of the period, not from a .TBL."><span>Enhanced props</span><span class=assets-viewer__count></span></button></li><li><button type=button class="assets-viewer__category assets-viewer__category--unbaked"><span>Unbaked</span><span class=assets-viewer__count>`),
  _tmpl$7 = /*#__PURE__*/_$template(`<li><button type=button class=assets-viewer__category><span></span><span class=assets-viewer__count>`),
  _tmpl$8 = /*#__PURE__*/_$template(`<li class=assets-viewer__empty>No assets match.`),
  _tmpl$9 = /*#__PURE__*/_$template(`<li><button type=button class=assets-viewer__entry>`);
import { ASSET_CATEGORIES } from "@red-opus/baker/schema";
import { createMemo, createResource, createSignal, For, Show } from "solid-js";
import { DetailPane } from "../assets/DetailPane.tsx";
import { groupByCategory, searchEntries } from "../assets/filter.ts";
import { nextIndex } from "../keyboard-nav.ts";
import { loadManifest } from "../assets/manifest.ts";
import { UnbakedList } from "../assets/viewers/UnbakedList.tsx";
import { PropViewer, propEntries } from "../assets/viewers/PropViewer.tsx";
import { formatCategoryLabel } from "../format-category-label.ts";

/**
 * Two entries that are not bake categories.
 *
 * `unbaked` is the honest-coverage list. `props` is the models in
 * `data/enhanced` that **no baked asset explains** — the searchlight (AD-017
 * §10) and the escort sloop (`docs/engineering/ship-design.md`), both built from
 * a citable object of the period because the original ships no asset for them.
 * They cannot appear under `models` for the reason they exist: there is nothing
 * in `data/baked/manifest.json` to hang them off, and this whole sidebar is that
 * manifest.
 */

/**
 * `/assets` — the baked-asset review suite (AD-001). Categories, counts,
 * and every listed asset come from `data/baked/manifest.json` (built by
 * `tools/bake`) — nothing here is hardcoded, so the sidebar can never
 * drift out of sync with what actually got baked.
 */
export function AssetsViewer() {
  const [manifest] = createResource(loadManifest);
  const [selection, setSelection] = createSignal("palettes");
  const [query, setQuery] = createSignal("");
  const [selectedIndex, setSelectedIndex] = createSignal(0);
  const categoryCounts = createMemo(() => groupByCategory(manifest()?.entries ?? [], ASSET_CATEGORIES));
  const unbakedCount = createMemo(() => manifest()?.unbaked.length ?? 0);
  /** Every id the bake produced, which is what tells a prop from a model with an enhanced counterpart. */
  const bakedIds = createMemo(() => new Set((manifest()?.entries ?? []).map(entry => entry.id)));
  const propCount = createMemo(() => propEntries(bakedIds()).length);
  // The prop pane owns a WebGPU renderer, so it is mounted lazily the first time
  // it is asked for and then **never unmounted** — `DetailPane`'s rule, and for
  // its measured reason: a full unmount/remount cycle of a pane holding a
  // `WebGPURenderer` retained ~4.4 MB of renderer-process RSS per switch that
  // never came back. Switching away only hides it.
  const [propsEverShown, setPropsEverShown] = createSignal(false);
  const visibleEntries = createMemo(() => {
    const current = selection();
    if (current === "unbaked" || current === "props") return [];
    const inCategory = (manifest()?.entries ?? []).filter(entry => entry.category === current);
    return searchEntries(inCategory, query());
  });
  const selectedEntry = createMemo(() => visibleEntries()[selectedIndex()]);
  function selectSidebar(next) {
    setSelection(next);
    if (next === "props") setPropsEverShown(true);
    setQuery("");
    setSelectedIndex(0);
  }
  function onListKeyDown(event) {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const direction = event.key === "ArrowDown" ? "down" : "up";
    setSelectedIndex(current => nextIndex(current, direction, visibleEntries().length));
  }
  return (() => {
    var _el$ = _tmpl$6(),
      _el$2 = _el$.firstChild,
      _el$3 = _el$2.firstChild,
      _el$4 = _el$3.nextSibling,
      _el$5 = _el$4.firstChild,
      _el$6 = _el$5.firstChild,
      _el$7 = _el$6.firstChild,
      _el$8 = _el$7.nextSibling,
      _el$9 = _el$5.nextSibling,
      _el$0 = _el$9.firstChild,
      _el$1 = _el$0.firstChild,
      _el$10 = _el$1.nextSibling;
    _$insert(_el$4, _$createComponent(For, {
      get each() {
        return categoryCounts();
      },
      children: entry => (() => {
        var _el$22 = _tmpl$7(),
          _el$23 = _el$22.firstChild,
          _el$24 = _el$23.firstChild,
          _el$25 = _el$24.nextSibling;
        _el$23.$$click = () => selectSidebar(entry.category);
        _$insert(_el$24, () => formatCategoryLabel(entry.category));
        _$insert(_el$25, () => entry.count);
        _$effect(() => _el$23.classList.toggle("assets-viewer__category--active", !!(selection() === entry.category)));
        return _el$22;
      })()
    }), _el$5);
    _el$6.$$click = () => selectSidebar("props");
    _$insert(_el$8, propCount);
    _el$0.$$click = () => selectSidebar("unbaked");
    _$insert(_el$10, unbakedCount);
    _$insert(_el$, _$createComponent(Show, {
      get when() {
        return manifest.error;
      },
      get children() {
        var _el$11 = _tmpl$(),
          _el$12 = _el$11.firstChild;
        _$insert(_el$11, () => String(manifest.error), null);
        return _el$11;
      }
    }), null);
    _$insert(_el$, _$createComponent(Show, {
      get when() {
        return manifest.loading;
      },
      get children() {
        return _tmpl$2();
      }
    }), null);
    _$insert(_el$, _$createComponent(Show, {
      get when() {
        return _$memo(() => !!manifest())() && selection() === "unbaked";
      },
      get children() {
        var _el$14 = _tmpl$3();
        _$insert(_el$14, _$createComponent(UnbakedList, {
          get entries() {
            return manifest().unbaked;
          }
        }));
        return _el$14;
      }
    }), null);
    _$insert(_el$, _$createComponent(Show, {
      get when() {
        return propsEverShown();
      },
      get children() {
        var _el$15 = _tmpl$4();
        _$insert(_el$15, _$createComponent(PropViewer, {
          get baked() {
            return bakedIds();
          }
        }));
        _$effect(_$p => _$setStyleProperty(_el$15, "display", selection() === "props" ? "" : "none"));
        return _el$15;
      }
    }), null);
    _$insert(_el$, _$createComponent(Show, {
      get when() {
        return _$memo(() => !!(manifest() && selection() !== "unbaked"))() && selection() !== "props";
      },
      get children() {
        var _el$16 = _tmpl$5(),
          _el$17 = _el$16.firstChild,
          _el$18 = _el$17.firstChild,
          _el$19 = _el$18.firstChild,
          _el$20 = _el$19.nextSibling,
          _el$21 = _el$18.nextSibling;
        _el$17.$$keydown = onListKeyDown;
        _el$19.$$input = event => {
          setQuery(event.currentTarget.value);
          setSelectedIndex(0);
        };
        _$insert(_el$20, () => visibleEntries().length);
        _$insert(_el$21, _$createComponent(For, {
          get each() {
            return visibleEntries();
          },
          get fallback() {
            return _tmpl$8();
          },
          children: (entry, index) => (() => {
            var _el$27 = _tmpl$9(),
              _el$28 = _el$27.firstChild;
            _el$28.$$click = () => setSelectedIndex(index());
            _$insert(_el$28, () => entry.id);
            _$effect(() => _el$28.classList.toggle("assets-viewer__entry--active", !!(index() === selectedIndex())));
            return _el$27;
          })()
        }));
        _$insert(_el$16, _$createComponent(DetailPane, {
          get entry() {
            return selectedEntry();
          }
        }), null);
        _$effect(() => _el$19.value = query());
        return _el$16;
      }
    }), null);
    _$effect(_p$ => {
      var _v$ = !!(selection() === "props"),
        _v$2 = !!(selection() === "unbaked");
      _v$ !== _p$.e && _el$6.classList.toggle("assets-viewer__category--active", _p$.e = _v$);
      _v$2 !== _p$.t && _el$0.classList.toggle("assets-viewer__category--active", _p$.t = _v$2);
      return _p$;
    }, {
      e: undefined,
      t: undefined
    });
    return _el$;
  })();
}
_$delegateEvents(["click", "keydown", "input"]);